import { useState, type ReactElement } from 'react';

import { Notice } from '@/components/admin/Notice';
import { ApiError } from '@/lib/api/http';

/**
 * パスワードを紛失した人を事務局が救済する操作（D-082）。
 * 担当者（/staff）と受講生（会員詳細）で同じ見た目・同じ順序にする。
 *
 * 事務局が本人のパスワードを見る手段は無い（保存しているのはハッシュだけ）。
 */
export function PasswordRecoveryBox({
  sendTo,
  onSendResetEmail,
  onIssueTemporary,
}: {
  /** 再設定メールの宛先（画面に出して送り先を確認できるようにする） */
  sendTo: string;
  onSendResetEmail: () => Promise<void>;
  onIssueTemporary: () => Promise<{ temporary_password: string }>;
}): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 発行した一時パスワード。画面を離れるまでの間だけ表示する（保存しない）
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);

  async function run(action: () => Promise<void>, fallback: string): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="mb-3 text-wptext-muted">
        事務局が本人のパスワードを見ることはできません（ハッシュだけを保存しています）。
        まずは再設定メールを試してください。
      </p>

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              setTemporaryPassword(null);
              await onSendResetEmail();
              setNotice(
                `${sendTo} に再設定メールを送りました。本人がリンクから新しいパスワードを設定します。`,
              );
            }, '再設定メールを送れませんでした。')
          }
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          再設定メールを送る
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const result = await onIssueTemporary();
              setTemporaryPassword(result.temporary_password);
            }, '一時パスワードを発行できませんでした。')
          }
          className="btn-secondary"
        >
          一時パスワードを発行する
        </button>
      </div>

      {temporaryPassword && (
        <div className="mt-3">
          <Notice kind="warning" dismissible={false}>
            <p className="mb-2">
              一時パスワードを発行しました。
              <strong>この画面を閉じると二度と表示されません。</strong>
              本人に伝えて、すぐに変更してもらってください。
            </p>
            <code className="inline-block rounded-sm border border-wpborder bg-white px-3 py-1.5 text-lg tracking-wider">
              {temporaryPassword}
            </code>
            <p className="mt-2 text-xs">
              発行時点で本人の既存のログイン状態はすべて解除されています。
            </p>
          </Notice>
        </div>
      )}
    </div>
  );
}
